/**
 *
 * @module adobeIdentityClient
 *
 */

import Check from 'check-types';
import Logging from './../../../logging/entry';
import Token from './../../token/entry';
import Exception from './../../exception/entry';
import HttpClient from './../../platforms/httpClient';

import AdobeIdentityClientConfiguration from './adobeIdentityClientConfiguration';
import AdobeRegistration from './adobeRegistration';

const InvalidArgumentException = Exception.InvalidArgumentException;
const ServiceException = Exception.ServiceException;

const AccessToken = Token.AccessToken;
const ErrorReason = Exception.ErrorReason;

const {
    nonEmptyString,
    instanceStrict
} = Check.assert;

/**
 *
 * @class AdobeIdentityClient
 * @implements {IAdobeIdentityClient}
 * @desc Provides a data client that can be used to access Adobe identity services.
 *
 */
export default class AdobeIdentityClient {

    /**
     *
     * @param {AdobeIdentityClientConfiguration} adobeIdentityClientConfiguration
     * @param {SDK.Logging.Logger} [logger=Logging]
     * @param {IHttpClient} [httpClient=null]
     *
     */
    constructor(adobeIdentityClientConfiguration, logger = Logging, httpClient = null) {

        const errorMessage = {
            ctor: 'AdobeIdentityClient(adobeIdentityClientConfiguration, logger, httpClient)',
            get adobeIdentityClientConfiguration() {
                return `${this.ctor} needs AdobeIdentityClientConfiguration instance`;
            }
        };

        instanceStrict(
            adobeIdentityClientConfiguration,
            AdobeIdentityClientConfiguration,
            errorMessage.adobeIdentityClientConfiguration,
            InvalidArgumentException
        );

        /**
         *
         * @access private
         * @type {AdobeIdentityClientConfiguration}
         * @desc The configuration information to use.
         *
         */
        this.config = adobeIdentityClientConfiguration;

        /**
         *
         * @access private
         * @type {SDK.Logging.Logger}
         *
         */
        this.logger = logger;

        /**
         *
         * @access private
         * @type {HttpClient}
         * @desc The object responsible for making HTTP requests.
         *
         */
        this.httpClient = httpClient || new HttpClient(logger);

        this.logger.log(AdobeIdentityClient.name, 'Created');

    }

    /**
     *
     * @access public
     * @param {SDK.Services.Token.AccessToken} accessToken - The access token to provide user context.
     * @desc Creates a registration code to use for adding an Adobe MVPD identity (only valid for a short period).
     * @returns {Promise<AdobeRegistration>} The response when the client has created a registration code.
     *
     */
    getAdobeRegistration(accessToken) {

        const errorMessage = {
            method: 'AdobeIdentityClient.getAdobeRegistration(accessToken)',
            get accessToken() {
                return `${this.method} needs SDK.Services.Token.AccessToken instance`;
            }
        };

        instanceStrict(accessToken, AccessToken, errorMessage.accessToken, InvalidArgumentException);

        return new Promise((resolve, reject) => {

            const reasons = [new ErrorReason('', `${errorMessage.method} - not-implemented`)];

            return reject(new ServiceException({ reasons }));

        });

    }

    /**
     *
     * @access public
     * @param {AdobeRegistration} adobeRegistration
     * @param {String} mvpdId - The service provider the user is subscribed to.
     * @param {SDK.Services.Token.AccessToken} accessToken - The access token to provide user context
     * @desc Creates an identity with Adobe for the given MVPD and registration code and return an id token.
     * @returns {Promise<GetIdentityTokenResult>} The response when the client attempts to create
     * an identity token from the adobe registration code
     *
     */
    getIdentityToken(adobeRegistration, mvpdId, accessToken) {

        const errorMessage = {
            method: 'AdobeIdentityClient.getIdentityToken(adobeRegistration, mvpdId, accessToken)',
            get adobeRegistration() {
                return `${this.method} needs AdobeRegistration instance`;
            },
            get mvpdId() {
                return `${this.method} mvpdId should be a String with length`;
            },
            get accessToken() {
                return `${this.method} needs SDK.Services.Token.AccessToken instance`;
            }
        };

        instanceStrict(adobeRegistration, AdobeRegistration, errorMessage.adobeRegistration, InvalidArgumentException);
        nonEmptyString(mvpdId, errorMessage.mvpdId, InvalidArgumentException);
        instanceStrict(accessToken, AccessToken, errorMessage.accessToken, InvalidArgumentException);

        return new Promise((resolve, reject) => {

            const reasons = [new ErrorReason('', `${errorMessage.method} - not-implemented`)];

            return reject(new ServiceException({ reasons }));

        });

    }

}



// WEBPACK FOOTER //
// ./src/services/identity/adobe/adobeIdentityClient.js